import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../../utils/bookingApi';
import { formatDayDate } from '../../../utils/dates';
import { PageHeader, Skeleton, EmptyState, ErrorState } from '../../../components/ui';
import '../booking-shared.css';
import './Safeguarding.css';

export const INCIDENT_TYPE_LABEL = {
  INJURY: 'Injury',
  NEAR_MISS: 'Near miss',
  ILLNESS: 'Illness',
  OTHER: 'Other',
};

export const SEVERITY_LABEL = {
  MINOR: 'Minor',
  MODERATE: 'Moderate',
  SEVERE: 'Severe',
};

const SEVERITY_TONE = { MINOR: 'info', MODERATE: 'wait', SEVERE: 'bad' };

export function SeverityPill({ severity }) {
  return (
    <span className={`bk-pill bk-pill--${SEVERITY_TONE[severity] || 'off'}`}>
      {SEVERITY_LABEL[severity] ?? severity}
    </span>
  );
}

export const INCIDENTS_PATH = '/booking/admin/incidents';

const excerpt = (text) => (text.length > 120 ? `${text.slice(0, 120)}…` : text);

export default function AdminIncidents() {
  const [incidents, setIncidents] = useState(null);
  const [error, setError] = useState(false);
  const [searchParams] = useSearchParams();

  const load = useCallback(() => {
    setError(false);
    bookingApi.getIncidents()
      .then(res => setIncidents(res.data))
      .catch((err) => { console.error('Could not load first aid reports', err); setError(true); });
  }, []);

  // Emails link to the list with ?id=; each report has its own page now
  const openId = searchParams.get('id');
  useEffect(() => { if (!openId) load(); }, [load, openId]);
  if (openId) return <Navigate to={`${INCIDENTS_PATH}/${openId}`} replace />;

  const header = (
    <PageHeader
      back={{ to: '/admin-hub', label: 'Admin' }}
      title="First aid reports"
      action={{ label: 'New report', short: 'New', to: `${INCIDENTS_PATH}/new` }}
    />
  );

  if (error) {
    return (
      <div className="bk-page bk-page--wide">
        {header}
        <ErrorState title="Couldn't load first aid reports" onRetry={load} />
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--wide">
      {header}
      {!incidents && <Skeleton rows={4} label="Loading first aid reports…" />}
      {incidents && incidents.length === 0 && (
        <EmptyState
          icon={ExclamationTriangleIcon}
          title="No first aid reports yet"
          text="When someone is hurt or unwell at the club, file a report here so there's a record and the family is told."
          action={<Link className="bk-btn bk-btn--primary" to={`${INCIDENTS_PATH}/new`}>New report</Link>}
        />
      )}
      {incidents && incidents.length > 0 && (
        <ul className="sg-list">
          {incidents.map(inc => {
            const name = inc.gymnast ? `${inc.gymnast.firstName} ${inc.gymnast.lastName}` : 'Unknown gymnast';
            return (
              <li key={inc.id}>
                <Link to={`${INCIDENTS_PATH}/${inc.id}`} className="bk-card sg-item">
                  <div className="sg-item__top">
                    <div>
                      <span className="sg-item__name">{name}</span>
                      <span className="bk-small"> {formatDayDate(inc.incidentDate)}</span>
                    </div>
                    <div className="sg-item__tags">
                      <SeverityPill severity={inc.severity} />
                      <span className="bk-small">{INCIDENT_TYPE_LABEL[inc.incidentType] ?? inc.incidentType}</span>
                    </div>
                  </div>
                  <p className="sg-item__text">{excerpt(inc.description)}</p>
                  <p className="sg-item__meta">
                    Filed by {inc.reportedBy.firstName} {inc.reportedBy.lastName}
                    {inc.forwards.length > 0 && ` · Forwarded to ${inc.forwards.length} recipient${inc.forwards.length !== 1 ? 's' : ''}`}
                  </p>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
